"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { motion } from "framer-motion";
import Logo from "@/components/Logo";
import { fetchMe, login, storeRole } from "@/lib/api";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email, password);
      const me = await fetchMe();
      storeRole(me.role);
      const dest =
        me.role === "admin" ? "/beheer"
        : me.role === "monteur" ? "/monteur"
        : me.role === "ep_centraal" ? "/ep"
        : "/opdrachtgever";
      router.replace(dest);
    } catch {
      setErr("Inloggen mislukt. Controleer e-mail en wachtwoord.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex flex-1 flex-col items-center justify-center px-6 py-16">
      <Link href="/" aria-label="Naar home" className="mb-8">
        <Logo size={22} />
      </Link>

      <motion.form
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 rounded-3xl border border-[var(--line)] bg-[var(--surface-raised)] p-8 shadow-lg"
      >
        <div className="space-y-1">
          <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-[var(--ink)]">
            Welkom terug
          </h1>
          <p className="text-[13px] text-[var(--ink-3)]">
            Log in op je MatchRite-account.
          </p>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-[12px] font-medium text-[var(--ink-2)]">E-mail</span>
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5 text-[14px] text-[var(--ink)] outline-none transition-colors focus:border-[var(--ink)]"
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[12px] font-medium text-[var(--ink-2)]">Wachtwoord</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5 text-[14px] text-[var(--ink)] outline-none transition-colors focus:border-[var(--ink)]"
          />
        </label>

        {err && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg bg-[#FEECEC] px-3 py-2 text-[13px] text-[#C0392B]"
          >
            {err}
          </motion.p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-full bg-[var(--ink)] px-4 py-3 text-[14px] font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
        >
          {busy ? "Bezig…" : "Inloggen"}
        </button>
      </motion.form>

      <p className="mt-6 text-[13px] text-[var(--ink-3)]">
        Nog geen account?{" "}
        <Link href="/signup" className="font-medium text-[var(--ink)] underline underline-offset-4 hover:text-[var(--brand-strong)]">
          Aanmelden
        </Link>
      </p>
    </main>
  );
}
